import { Fragment, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type {
  RunIssueAcceptanceStatus,
  RunIssueViewModel
} from './RunPage';

type IssueSeverity = 'critical' | 'high' | 'medium' | 'low' | 'informational' | 'gas' | 'other';
type IssueSeverityFilter = 'all' | IssueSeverity;

interface RunIssueDashboardProps {
  issues: RunIssueViewModel[];
  markdownExportUrl: string;
  pdfExportUrl: string;
  onIssueAcceptanceChange: (issueId: string, status: RunIssueAcceptanceStatus | null) => Promise<void> | void;
}

interface RunIssueViewModelEnhanced extends RunIssueViewModel {
  severity: IssueSeverity;
  cleanTitle: string;
}

type IssueAcceptanceMode = 'open' | RunIssueAcceptanceStatus;

const SEVERITY_ORDER: IssueSeverity[] = [
  'critical', 'high', 'medium', 'low', 'informational', 'gas', 'other'
];

const SEVERITY_LABELS: Record<IssueSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  informational: 'Info',
  gas: 'Gas',
  other: 'Other',
};

const ACCEPTANCE_LABELS: Record<RunIssueAcceptanceStatus, string> = {
  acknowledged: 'Acknowledged',
  accepted: 'Accepted',
};

const ISSUES_TABS: { filter: IssueSeverityFilter; label: string }[] = [
  { filter: 'all', label: 'All Issues' },
  { filter: 'critical', label: 'Critical' },
  { filter: 'high', label: 'High' },
  { filter: 'medium', label: 'Medium' },
  { filter: 'low', label: 'Low' },
  { filter: 'informational', label: 'Informational' },
  { filter: 'gas', label: 'Gas' },
];

const runIssueParseSeverity = (title: string, impact: string): IssueSeverity => {
  const m = title.match(/^\[(Critical|High|Medium|Low|Informational|Info|Gas)\]/i);
  if (m) {
    const s = m[1].toLowerCase();
    return (s === 'info' ? 'informational' : s) as IssueSeverity;
  }

  const imp = impact.toLowerCase();
  if (imp.includes('critical')) return 'critical';
  if (imp.includes('high')) return 'high';
  if (imp.includes('medium')) return 'medium';
  if (imp.includes('low')) return 'low';
  if (imp.includes('info')) return 'informational';
  if (imp.includes('gas')) return 'gas';
  return 'other';
};

const runIssueMapBackendSeverity = (severity: string | undefined): IssueSeverity | null => {
  if (!severity) {
    return null;
  }

  switch (severity.toLowerCase()) {
    case 'critical': return 'critical';
    case 'high': return 'high';
    case 'medium': return 'medium';
    case 'low': return 'low';
    case 'info':
    case 'informational': return 'informational';
    case 'gas': return 'gas';
    default: return null;
  }
};

const runIssueCleanTitle = (title: string): string =>
  title
    .replace(/^\[(Critical|High|Medium|Low|Informational|Info|Gas)\]\s*/i, '')
    .replace(/^(Critical|High|Medium|Low|Informational|Info|Gas)\s*[-\u2013\u2014]\s*/i, '')
    .trim();

const runIssueEnhanceIssues = (issues: RunIssueViewModel[]): RunIssueViewModelEnhanced[] =>
  issues
    .map((issue) => ({
      ...issue,
      severity: runIssueMapBackendSeverity(issue.severity) ?? runIssueParseSeverity(issue.title, issue.impact),
      cleanTitle: runIssueCleanTitle(issue.title),
    }))
    .sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity));

function IconList() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
      <line x1="3" y1="5" x2="15" y2="5" />
      <line x1="3" y1="9" x2="15" y2="9" />
      <line x1="3" y1="13" x2="15" y2="13" />
    </svg>
  );
}

function IconFlame() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
      <path d="M9 1.5C8 4 6 6 6 9a3 3 0 006 0c0-1-.3-1.8-.7-2.5-.5 1-1.3 1.8-2.3 2 .5-1.5.5-3.5 0-7z" />
      <ellipse cx="9" cy="13" rx="1.5" ry="1.5" />
    </svg>
  );
}

function IconTriangleFilled() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
      <path d="M9 2.5L2 15.5h14L9 2.5z" />
      <line x1="9" y1="8" x2="9" y2="11.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="9" cy="13.5" r="0.9" fill="white" />
    </svg>
  );
}

function IconTriangleOutline() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 3L2.5 15h13L9 3z" />
      <line x1="9" y1="8" x2="9" y2="11" />
      <circle cx="9" cy="13" r="0.75" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2L3 4.5v4.8C3 13 5.8 15.6 9 16.5c3.2-.9 6-3.5 6-7.2V4.5L9 2z" />
    </svg>
  );
}

function IconInfoCircle() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
      <circle cx="9" cy="9" r="7" />
      <line x1="9" y1="8.5" x2="9" y2="12.5" />
      <circle cx="9" cy="6" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconFileSmall() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V6L9 2z" />
      <path d="M9 2v4h4" />
    </svg>
  );
}

function IconDownload() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2.5v8" />
      <path d="M5.75 7.75L9 11l3.25-3.25" />
      <path d="M3.25 13.5v1.25c0 .7.55 1.25 1.25 1.25h9c.7 0 1.25-.55 1.25-1.25V13.5" />
    </svg>
  );
}

function SeverityTabIcon({ filter }: { filter: IssueSeverityFilter }) {
  if (filter === 'all') return <IconList />;
  if (filter === 'critical') return <IconFlame />;
  if (filter === 'high') return <IconTriangleFilled />;
  if (filter === 'medium') return <IconTriangleOutline />;
  if (filter === 'low') return <IconShield />;
  return <IconInfoCircle />;
}

function RunMdText({ text }: { text: string }) {
  const regex = /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  const parts: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;

  regex.lastIndex = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      parts.push(<code key={m.index} className="md-code">{m[1]}</code>);
    } else if (m[2] !== undefined) {
      parts.push(<strong key={m.index}>{m[2]}</strong>);
    } else if (m[3] !== undefined) {
      parts.push(<em key={m.index}>{m[3]}</em>);
    }
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));

  return <>{parts}</>;
}

function RunMdBlock({ text }: { text: string }) {
  const paras = text.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);

  return (
    <>
      {paras.map((para, i) => (
        <p key={i}>
          <RunMdText text={para.replace(/\n/g, ' ')} />
        </p>
      ))}
    </>
  );
}

function SeverityBadge({ severity }: { severity: IssueSeverity }) {
  return (
    <span className={`sev-badge sev-${severity}`}>
      {SEVERITY_LABELS[severity]}
    </span>
  );
}

function AcceptanceBadge({ status }: { status: RunIssueAcceptanceStatus }) {
  return (
    <span className={`acceptance-badge acceptance-${status}`}>
      {ACCEPTANCE_LABELS[status]}
    </span>
  );
}

function AcceptanceSegment({
  value,
  disabled,
  onChange,
}: {
  value: IssueAcceptanceMode;
  disabled: boolean;
  onChange: (value: IssueAcceptanceMode) => void;
}) {
  const options: { value: IssueAcceptanceMode; label: string }[] = [
    { value: 'open', label: 'Open' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'accepted', label: 'Accepted' },
  ];

  return (
    <div className="issue-acceptance-segment" role="group" aria-label="Issue review status">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`issue-acceptance-option${value === option.value ? ' active' : ''}`}
          disabled={disabled}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function RunIssueDetail({
  issue,
  isAcceptanceUpdating,
  acceptanceError,
  onAcceptanceChange,
}: {
  issue: RunIssueViewModelEnhanced;
  isAcceptanceUpdating: boolean;
  acceptanceError: string;
  onAcceptanceChange: (status: RunIssueAcceptanceStatus | null) => void;
}) {
  return (
    <div className="issue-report">
      <div className="issue-report-header">
        <div className="issue-report-statuses">
          <SeverityBadge severity={issue.severity} />
          {issue.acceptanceStatus && <AcceptanceBadge status={issue.acceptanceStatus} />}
        </div>
        <h2 className="issue-report-title">{issue.cleanTitle}</h2>
        <AcceptanceSegment
          value={issue.acceptanceStatus ?? 'open'}
          disabled={isAcceptanceUpdating}
          onChange={(status) => onAcceptanceChange(status === 'open' ? null : status)}
        />
        {acceptanceError && (
          <div className="issue-acceptance-error" role="alert">
            {acceptanceError}
          </div>
        )}
      </div>

      <hr className="issue-report-divider" />

      <div className="issue-report-section">
        <h3>Summary</h3>
        <RunMdBlock text={issue.summary} />
      </div>

      {issue.detail && (
        <div className="issue-report-section">
          <h3>Detail</h3>
          <RunMdBlock text={issue.detail} />
        </div>
      )}

      <div className="issue-report-section">
        <h3>Impact</h3>
        <RunMdBlock text={issue.impact} />
      </div>

      {issue.files.length > 0 && (
        <div className="issue-report-section">
          <h3>Affected Files</h3>
          <div className="issue-report-files">
            {issue.files.map((file) => (
              <span key={file} className="issue-report-file">
                <IconFileSmall />
                {file}
              </span>
            ))}
          </div>
        </div>
      )}

      {issue.sources.length > 0 && (
        <div className="issue-report-section">
          <h3>Sources</h3>
          <div className="issue-report-sources">
            {issue.sources.map((src) => (
              <span key={src} className="issue-report-source-tag">{src}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RunIssueDashboard({
  issues,
  markdownExportUrl,
  pdfExportUrl,
  onIssueAcceptanceChange
}: RunIssueDashboardProps) {
  const enhancedIssues = runIssueEnhanceIssues(issues);
  const [filter, setFilter] = useState<IssueSeverityFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(enhancedIssues[0]?.id ?? null);
  const [acceptanceError, setAcceptanceError] = useState('');
  const [updatingAcceptanceIssueId, setUpdatingAcceptanceIssueId] = useState<string | null>(null);

  const filteredIssues = filter === 'all'
    ? enhancedIssues
    : enhancedIssues.filter((i) => i.severity === filter);

  const selectedIssue = filteredIssues.find((i) => i.id === selectedId)
    ?? filteredIssues[0]
    ?? null;

  const handleFilterChange = (newFilter: IssueSeverityFilter) => {
    setFilter(newFilter);
    const first = newFilter === 'all'
      ? enhancedIssues[0]
      : enhancedIssues.find((i) => i.severity === newFilter);
    setSelectedId(first?.id ?? null);
  };

  const handleAcceptanceChange = async (
    issue: RunIssueViewModelEnhanced,
    nextStatus: RunIssueAcceptanceStatus | null
  ) => {
    setAcceptanceError('');
    setUpdatingAcceptanceIssueId(issue.id);

    try {
      await onIssueAcceptanceChange(issue.id, nextStatus);
    } catch (error) {
      setAcceptanceError(error instanceof Error ? error.message : String(error));
    } finally {
      setUpdatingAcceptanceIssueId(null);
    }
  };

  useEffect(() => {
    setAcceptanceError('');
  }, [selectedIssue?.id]);

  const severitiesPresent = new Set(enhancedIssues.map((i) => i.severity));

  const visibleTabs = ISSUES_TABS.filter(
    (tab) => tab.filter === 'all' || severitiesPresent.has(tab.filter as IssueSeverity)
  );

  const activeLabel = ISSUES_TABS.find((t) => t.filter === filter)?.label ?? 'Issues';

  return (
    <div className="issues-screen">
      <div className="issues-columns">
        <nav className="issues-tab-rail" aria-label="Filter by severity">
          {visibleTabs.map((tab, index) => (
            <Fragment key={tab.filter}>
              {index === 1 && <div className="issues-tab-divider" role="separator" />}
              <button
                type="button"
                className={`issues-tab-btn${tab.filter !== 'all' ? ` sev-${tab.filter}` : ''}${filter === tab.filter ? ' active' : ''}`}
                title={tab.label}
                aria-pressed={filter === tab.filter}
                onClick={() => handleFilterChange(tab.filter)}
              >
                <SeverityTabIcon filter={tab.filter} />
              </button>
            </Fragment>
          ))}
        </nav>

        <div className="issues-list-panel">
          <div className="issues-list-header">
            <div className="issues-list-title">
              <h2>{activeLabel}</h2>
              <span className="issues-list-count">{filteredIssues.length}</span>
            </div>
            <div className="issues-export-actions" aria-label="Export report">
              <a
                className="issues-export-link"
                href={markdownExportUrl}
                aria-label="Export Markdown report"
              >
                <IconDownload />
                <span>MD</span>
              </a>
              <a
                className="issues-export-link"
                href={pdfExportUrl}
                aria-label="Export PDF report"
              >
                <IconDownload />
                <span>PDF</span>
              </a>
            </div>
          </div>

          <div className="issues-list-scroll" role="list">
            {filteredIssues.length === 0 ? (
              <div className="issues-list-empty">No issues in this category.</div>
            ) : (
              filteredIssues.map((issue) => (
                <button
                  key={issue.id}
                  type="button"
                  role="listitem"
                  className={`issues-list-item${selectedIssue?.id === issue.id ? ' selected' : ''}${issue.acceptanceStatus ? ` is-${issue.acceptanceStatus}` : ''}`}
                  onClick={() => setSelectedId(issue.id)}
                >
                  <div className="issues-list-item-top">
                    <SeverityBadge severity={issue.severity} />
                    <span className="issues-list-item-id">#{issue.id}</span>
                    {issue.acceptanceStatus && <AcceptanceBadge status={issue.acceptanceStatus} />}
                  </div>
                  <span className="issues-list-item-title">{issue.cleanTitle}</span>
                  {issue.sources.length > 0 && (
                    <span className="issues-list-item-meta">{issue.sources.join(' · ')}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        <div className="issues-detail-panel">
          {selectedIssue ? (
            <div className="issues-detail-scroll">
              <RunIssueDetail
                issue={selectedIssue}
                isAcceptanceUpdating={updatingAcceptanceIssueId === selectedIssue.id}
                acceptanceError={acceptanceError}
                onAcceptanceChange={(nextStatus) => void handleAcceptanceChange(selectedIssue, nextStatus)}
              />
            </div>
          ) : (
            <div className="issues-detail-empty">
              <span>Select an issue to view details.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RunIssueDashboard;
